import {
  ChangeDetectionStrategy, Component, computed, HostBinding, Inject, Input, OnInit, Signal, signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs';

import { PieceProductSettings } from 'extensions/core/piece-product/front/models/piece-product.models';
import { PieceProductCarouselSettings } from 'extensions/core/piece-product-carousel/front/models/piece-product-carousel.model';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { RenderSetupDataService } from 'idt-ecommerce-api/render-setup-data/render-setup-data.service';
import { StoreLanguage } from 'idt-ecommerce-api/stores/stores.models';
import { getConfigurationParam, getStringParam, getBooleanParam } from 'render/render-shared/ui/render-product-prices/render-product-prices-utils';
import { GrowthbookConfig, RenderGrowthbookConfigsService } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { InditexWindow } from 'shared/models/inditex-window.models';
import {
  PRODUCT_DEFAULT_PRODUCT_PRICE_FORMAT,
  PRODUCT_DEFAULT_OLD_PRODUCT_PRICE_FORMAT,
  PRODUCT_DEFAULT_FUTURE_PRODUCT_PRICE_FORMAT,
  PRODUCT_DEFAULT_PRODUCT_PREWARMING_DESCRIPTION_FORMAT,
  PRODUCT_DEFAULT_ORIGINAL_PRODUCT_PRICE_FORMAT,
  PRODUCT_DEFAULT_PRICE_DISCOUNT_FORMAT,
  PRODUCT_DEFAULT_PRODUCT_PREWARMING_DISCOUNT_RATE_FORMAT
} from 'shared/models/product-prices/product-prices.models';
import { ProductsPricesService } from 'shared/services/products/products-prices.service';

import { templateUrl, styleUrls } from './render-product-prices.component.paths';


@Component({
  selector: 'render-product-prices',
  templateUrl,
  styleUrls,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--background-color-future-price]': 'backgroundColorFuturePrice()',
    '[style.--color-future-price]': 'colorFuturePrice()',
    '[style.--color-original-product]': 'colorOriginalProduct()',
    '[style.--color-locale-product]': 'colorLocaleProduct()',
    '[style.--color-current-product]': 'colorCurrentProduct()',
    '[style.--background-discount-product]': 'backgroundColorDiscountProduct()',
    '[style.--color-discount-product]': 'colorDiscountProduct()'
  }
})
export class RenderProductPricesComponent extends UnsubscribeComponent implements OnInit {

  @Input() public set piece(value: IdtComponent<PieceProductCarouselSettings | PieceProductSettings> | undefined) {
    this.pieceSignal.set(value);
  }

  public get piece(): IdtComponent<PieceProductCarouselSettings | PieceProductSettings> | undefined {
    return this.pieceSignal();
  }

  @Input() public set product(value: Product) {
    this.productSignal.set(value);
  }

  public get product(): Product {
    return this.productSignal()!;
  }

  @Input() public set customStyles(value: boolean) {
    this.customStylesSignal.set(value);
  }

  public get customStyles(): boolean {
    return this.customStylesSignal();
  }

  public direction: 'ltr' | 'rtl' = 'ltr';
  public locale: string = '';

  public readonly backgroundColorFuturePrice = computed(() => {
    if (this.isVisibleFuturePrice) {
      return getStringParam(this.configs(), 'product.price.future.color-background', 'initial');
    }
    return 'initial';
  });

  public readonly colorFuturePrice = computed((): string | undefined => {
    if (this.isVisibleFuturePrice) {
      return getStringParam(this.configs(), 'product.price.future.color-text');
    }
    return undefined;
  });

  public readonly colorOriginalProduct = computed(() => {
    if (this.customStylesSignal()) {
      return getStringParam(this.configs(), 'content.home-product.secondary-color', '#ffffff');
    }
    return getStringParam(this.configs(), 'content.home-carrusel.secondary-color', '#707070');
  });

  public readonly colorLocaleProduct = computed(() => {
    if (this.customStylesSignal()) {
      return getStringParam(this.configs(), 'content.home-product.primary-color', '#ffffff');
    }
    return getStringParam(this.configs(), 'content.home-carrusel.primary-color', '#000000');
  });

  public readonly colorCurrentProduct = computed(() => {
    const colorCurrentProduct = this.customStylesSignal()
      ? getStringParam(this.configs(), 'content.home-product.quaternary-color', '#ffffff')
      : getStringParam(this.configs(), 'content.home-carrusel.quaternary-color', '#D0024C');
    return !this.isVisibleOldPrice || this.isVisibleFuturePrice ? this.colorLocaleProduct() : colorCurrentProduct;
  });

  public readonly backgroundColorDiscountProduct = computed(() => `${this.colorDiscountProduct()}1A`);

  public readonly colorDiscountProduct = computed(() => this.colorCurrentProduct());

  private readonly configs: Signal<GrowthbookConfig[]> = toSignal(
    this.renderGrowthbookService.configurations$,
    { initialValue: [] as GrowthbookConfig[] }
  );

  private readonly pieceSignal = signal<
    IdtComponent<PieceProductCarouselSettings | PieceProductSettings
    > | undefined>(undefined);

  private readonly productSignal = signal<Product | undefined>(undefined);
  private readonly customStylesSignal = signal(false);

  constructor(
    @Inject('Window') private readonly window: InditexWindow,
    private readonly productsPricesService: ProductsPricesService,
    private readonly contextService: ContextService,
    private readonly renderGrowthbookService: RenderGrowthbookConfigsService,
    private readonly renderSetupDataService: RenderSetupDataService
  ) {
    super();
  }

  @HostBinding('style.--prices-flex-direction')
  public get displayProductPricesInTwoLines(): 'column' | 'row' {
    return (this.piece!.settings!.general as PieceProductCarouselSettings['general']).displayProductMultiplePricesTwoLines === true
      ? 'column'
      : 'row';
  }

  public get isVisibleOriginalPrice(): boolean {
    const enabledTriplePrice = getConfigurationParam(this.configs(), 'product.price.triple.enabled');

    if (enabledTriplePrice !== undefined) {
      return this.isTrue(enabledTriplePrice) && !!this.product.priceInfo?.originalPriceWithCurrencySymbol;
    }
    return !!this.piece!.settings!.general.displayOriginalProductPrice
      && !!this.product.priceInfo?.originalPriceWithCurrencySymbol;
  }

  public get isVisibleOldPrice(): boolean {
    return !!this.piece!.settings!.general.displayOldProductPrices
      && !!this.product.priceInfo?.oldPriceWithCurrencySymbol;
  }

  public get isVisibleFuturePrice(): boolean {
    const futurePriceEnabled = getConfigurationParam(this.configs(), 'product.price.future.enabled');

    if (futurePriceEnabled !== undefined) {
      return this.isTrue(futurePriceEnabled) && !!this.product.priceInfo?.prewarming?.priceWithCurrency;
    }
    return !!this.piece!.settings!.general.displayFutureProductPrices
      && this.isVisiblePrewarmingWhenDiscountApplied
      && !!this.product.priceInfo?.prewarming?.priceWithCurrency;
  }

  public get isVisiblePrewarmingDescription(): boolean {
    return !!this.piece!.settings!.general.displayProductPrewarmingDescription
      && this.isVisiblePrewarmingWhenDiscountApplied
      && !!this.product.priceInfo?.prewarming?.description;
  }

  public get isVisiblePrewarmingDiscountRate(): boolean {
    return !!this.piece!.settings!.general.displayProductPrewarmingDiscountRate
      && this.isVisiblePrewarmingWhenDiscountApplied
      && !!this.product.priceInfo?.prewarming?.discountRate;
  }

  public get isVisiblePrewarmingWhenDiscountApplied(): boolean {
    const hasOldPrice = !!this.product.priceInfo?.oldPrice;
    const shouldHidePrewarming = this.piece?.settings?.general.hidePrewarmingWhenDiscountApplied;
    return !hasOldPrice || (hasOldPrice && !shouldHidePrewarming);
  }

  public get isVisibleProductPriceDiscount(): boolean {
    const promoPercentageShow = getConfigurationParam(this.configs(), 'product.price.percentage.show');

    if (promoPercentageShow !== undefined) {
      return this.isTrue(promoPercentageShow) && !!this.product.priceInfo?.oldPriceWithCurrencySymbol;
    }
    return this.isVisibleOldPrice
      && !!this.piece!.settings!.general.displayPriceDiscount;
  }

  public get isVisibleDoublePriceDiscount(): boolean {
    const triplePricePercentage = getConfigurationParam(this.configs(), 'product.price.triple.percentage');
    return typeof triplePricePercentage === 'string'
      ? this.isVisibleOriginalPrice && triplePricePercentage.toUpperCase() === 'ORIGINAL'
      : false;
  }

  public get isVisibleTriplePriceDiscount(): boolean {
    const triplePricePercentage = getConfigurationParam(this.configs(), 'product.price.triple.percentage');
    return typeof triplePricePercentage === 'string'
      ? this.isVisibleOriginalPrice && triplePricePercentage.toUpperCase() === 'CUT'
      : false;
  }

  public get isVisibleTriplePriceRemark(): boolean {
    if (this.product.priceInfo?.promotion?.promotionId && !this.isVisibleOriginalPrice && !this.isVisibleFuturePrice
      && !this.product.priceInfo?.oldPrice && !this.product.priceInfo?.originalPrice) {
      return getBooleanParam(this.configs(), 'product.price.triple.remark');
    }
    return false;
  }

  public get isVisiblePriceDescription(): boolean {
    return this.isVisibleOriginalPrice && !!this.piece!.settings!.general.displayProductPriceDescription;
  }

  public get isVisibleProductPromotionDates(): boolean {
    return getBooleanParam(this.configs(), 'product.price.promotions.date.enabled');
  }

  public ngOnInit(): void {
    this.contextService.getCurrentLanguage$().pipe(
      distinctUntilChanged(),
      filter((currentLang: StoreLanguage) => !!currentLang?.direction),
      takeUntil(this.clearSubscriptions$)
    ).subscribe((currentLang: StoreLanguage) => {
      this.direction = currentLang.direction;
      this.locale = currentLang.code;
    });
  }

  public getTextProductPrice(isLocale: boolean, isCurrentPriceOnSale: boolean = false): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_PRODUCT_PRICE_FORMAT,
      'productPrice',
      isLocale
        ? this.product.priceInfo!.localePriceWithCurrencySymbol!
        : this.product.priceInfo!.priceWithCurrencySymbol!,
      isCurrentPriceOnSale
        ? this.piece!.settings!.general.productPriceOnSaleTextFormat
        : this.piece!.settings!.general.productPricesTextFormat
    );
  }

  public getTextOldProductPrice(isLocale: boolean): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_OLD_PRODUCT_PRICE_FORMAT,
      'oldProductPrice',
      isLocale
        ? this.product.priceInfo!.oldLocalePriceWithCurrencySymbol!
        : this.product.priceInfo!.oldPriceWithCurrencySymbol!,
      this.piece!.settings!.general.oldProductPricesTextFormat
    );
  }

  public getTextFutureProductPrice(isLocale: boolean): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_FUTURE_PRODUCT_PRICE_FORMAT,
      'futureProductPrice',
      isLocale
        ? this.product.priceInfo!.prewarming!.localePriceWithCurrency!
        : this.product.priceInfo!.prewarming!.priceWithCurrency,
      this.piece!.settings!.general.futureProductPricesTextFormat
    );
  }

  public getTextProductPrewarmingDescription(): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_PRODUCT_PREWARMING_DESCRIPTION_FORMAT,
      'productPrewarmingDescription',
      this.product.priceInfo!.prewarming!.description!,
      this.piece!.settings!.general.productPrewarmingDescriptionTextFormat
    );
  }

  public getTextOriginalProductPrice(): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_ORIGINAL_PRODUCT_PRICE_FORMAT,
      'originalProductPrice',
      this.product.priceInfo!.originalPriceWithCurrencySymbol!,
      this.piece!.settings!.general.originalProductPriceTextFormat
    );
  }

  public getDiscountDirection(discount: string): string {
    return this.direction === 'ltr' ? `-${discount}%` : `${discount}%-`;
  }

  public getTextProductPrewarmingDiscountRate(): string {
    if (this.product.priceInfo?.discountsPercentages) {
      const discount = this.product.priceInfo?.discountsPercentages?.oldFuturePriceDiscount;
      const template = this.piece!.settings!.general.productPrewarmingDiscountRateTextFormat
        ?? PRODUCT_DEFAULT_PRODUCT_PREWARMING_DISCOUNT_RATE_FORMAT;
      const format = discount ? this.getDiscountDirection(discount) : '';
      return template.replace(/{{productPrewarmingDiscountRate}}/g, format);
    }
    return this.productsPricesService.calculatePrewarmingDiscount(
      this.piece!.settings!.general.prewarmingDiscountOption,
      this.piece!.settings!.general.productPrewarmingDiscountRateTextFormat,
      this.product
    );
  }

  public getProductPriceDiscountTextFormat(): string {
    if (this.product.priceInfo?.discountsPercentages) {
      const discount = this.piece!.settings!.general.priceDiscountOption === 'old-price'
        ? this.product.priceInfo?.discountsPercentages?.oldPriceDiscount
        : this.product.priceInfo?.discountsPercentages?.originalPriceDiscount;
      const template = this.piece!.settings!.general.priceDiscountTextFormat ?? PRODUCT_DEFAULT_PRICE_DISCOUNT_FORMAT;
      const format = discount ? this.getDiscountDirection(discount) : '';
      return template.replace(/{{productPriceDiscount}}/g, format);
    }
    return this.productsPricesService.calculateActualPriceDiscount(
      this.piece!.settings!.general.priceDiscountOption,
      this.piece!.settings!.general.priceDiscountTextFormat,
      this.product
    );
  }

  public getProductPriceDiscount(): string {
    if (this.product.priceInfo?.discountsPercentages) {
      const discount = this.product.priceInfo?.discountsPercentages?.oldPriceDiscount;
      const template = this.piece?.settings?.general.oldProductPricesTextFormat
        ?? PRODUCT_DEFAULT_OLD_PRODUCT_PRICE_FORMAT;
      const format = discount ? this.getDiscountDirection(discount) : '';
      return template.replace(/{{oldProductPrice}}/g, format);
    }
    return '';
  }

  public getTextProductPromotionDescription(): string {
    if (this.product.priceInfo?.promotion?.startDate && this.product.priceInfo?.promotion?.endDate) {
      const format: Intl.DateTimeFormatOptions = {
        month: '2-digit',
        day: '2-digit'
      };

      const localeStartDate = new Date(this.product.priceInfo.promotion.startDate).toLocaleDateString(
        this.locale,
        format
      );
      const localeEndDate = new Date(this.product.priceInfo.promotion.endDate).toLocaleDateString(
        this.locale,
        format
      );

      const translations = this.renderSetupDataService.translations;
      const promotionDatesTemplate = translations?.['product-card.promotion-dates'];
      const message = typeof promotionDatesTemplate === 'string'
        ? promotionDatesTemplate.replace(/\{(\d+)\}/g, (match: string, index: string) => {
            const values = [localeStartDate, localeEndDate];
            const replacement = values[Number(index)];
            return replacement ?? match;
          })
        : this.window.inditex?.text?.('ItxProductPageComponent.promotionsDates', localeStartDate, localeEndDate);

      const dates = message ?? '';


      return this.productsPricesService.getTextFormat(
        PRODUCT_DEFAULT_PRODUCT_PREWARMING_DESCRIPTION_FORMAT,
        'productPrewarmingDescription',
        dates
      );
    }
    return '';
  }

  private isTrue(value: string | boolean | undefined): boolean {
    return value === true || value === 'true' || value === 'TRUE';
  }

}
